/**
 * Campaign Analytics · Feb 2026 · R135
 *
 * Tracks bulk WhatsApp + Bulk Email blasts fired from the Leads
 * screen. Table by default, click a row to open a detail drawer
 * with the full per-recipient result timeline.
 */
import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { apiErrorMessage } from "@/lib/apiError";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import EmptyState from "@/components/EmptyState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Rocket, Mail, MessageCircle, RefreshCw, Search, TrendingUp, Clock, CheckCircle2, AlertTriangle, MinusCircle, Reply, Download, Copy, Ban, CheckCheck } from "lucide-react";
import { waErrorInfo, parseWaReason } from "@/lib/waErrors";

const CHANNEL_ICON = { email: Mail, whatsapp: MessageCircle };

function formatDateShort(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}


/**
 * R267c · Feb 2026 — Per-recipient WA delivery timeline chip.
 *
 * Renders three ticks (sent → delivered → read) coloured against
 * the row's actual state. `delivery_status_at` is the wall-clock
 * time Interakt fired the delivered/read webhook (captured by
 * `_propagate_wa_status_to_campaign`), so we can show real
 * elapsed lag in hover — great for spotting "delivered but not
 * read for 3 days" trends.
 */
function WaDeliveryTimeline({ row }) {
  const failed = row.status === "failed" || row.delivery_status === "failed";
  const sent = row.status === "sent" || !!row.delivery_status;
  const delivered = row.delivery_status === "delivered" || row.delivery_status === "read";
  const read = row.delivery_status === "read";
  const stateFor = (on, kind) => on ? (kind === "read" ? "text-blue-600" :
                                        kind === "failed" ? "text-rose-600" :
                                        "text-emerald-600")
                                    : "text-neutral-300";
  const at = row.delivery_status_at || row.at;
  const _tip = failed
    ? `Meta rejected · ${formatDateShort(at)}`
    : read
    ? `Read · ${formatDateShort(at)}`
    : delivered
    ? `Delivered · ${formatDateShort(at)}`
    : sent
    ? `Sent · ${formatDateShort(row.at)}`
    : "Not sent yet";
  return (
    <div className="flex items-center gap-0.5 font-mono text-[11px]"
         title={_tip} data-testid="wa-delivery-timeline">
      <CheckCircle2 className={`w-3 h-3 shrink-0 ${stateFor(sent && !failed, "sent")}`} />
      <CheckCheck className={`w-3 h-3 shrink-0 ${stateFor(delivered && !failed, "delivered")}`} />
      <CheckCheck className={`w-3 h-3 shrink-0 ${stateFor(read && !failed, "read")}`} />
      {failed && <Ban className="w-3 h-3 shrink-0 text-rose-600 ml-0.5" />}
      <span className="ml-1.5 text-[10px] text-muted-foreground truncate max-w-[110px]">
        {failed ? "meta-rejected" : read ? "read" : delivered ? "delivered" : sent ? "sent" : "queued"}
      </span>
    </div>
  );
}


/** R267d · Feb 2026 — Delivery Insights card.
 *
 * Aggregates the workspace's last-30-days WA campaigns by template
 * and surfaces p50 delivery + read latency plus delivery / read /
 * failure rates. Sits at the top of the Campaigns page so PMs can
 * spot the slowest big-volume templates at a glance without
 * clicking into every campaign.
 */
function WaDeliveryInsightsCard({ rows, loading, onRefresh }) {
  const list = rows || [];
  if (!loading && list.length === 0) return null;
  const fmtMs = (ms) => {
    if (ms == null) return "—";
    if (ms < 1000) return `${ms}ms`;
    if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`;
    if (ms < 3_600_000) return `${(ms / 60_000).toFixed(1)}m`;
    return `${(ms / 3_600_000).toFixed(1)}h`;
  };
  const fmtRate = (r) => r == null ? "—" : `${Math.round(r * 100)}%`;
  const rateColour = (r) => r == null ? "text-muted-foreground" :
    r >= 0.85 ? "text-emerald-700" :
    r >= 0.6 ? "text-amber-700" : "text-rose-700";
  return (
    <div className="border border-blue-300 bg-blue-50/40" data-testid="campaigns-delivery-insights">
      <div className="px-3 py-1.5 border-b border-blue-200 text-[10px] uppercase tracking-widest text-blue-800 flex items-center gap-2">
        <Clock className="w-3 h-3" /> WhatsApp delivery insights · last 30 days
        <button onClick={onRefresh} disabled={loading}
                className="ml-auto normal-case tracking-normal underline text-[11px]"
                data-testid="campaigns-insights-refresh">
          {loading ? "Loading…" : "Refresh"}
        </button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-blue-100/50 text-blue-900">
            <tr className="text-left">
              <th className="px-3 py-1.5">Template</th>
              <th className="px-3 py-1.5 text-right">Sent</th>
              <th className="px-3 py-1.5 text-right">Delivered</th>
              <th className="px-3 py-1.5 text-right">Read</th>
              <th className="px-3 py-1.5 text-right">Failed</th>
              <th className="px-3 py-1.5 text-right" title="Median time from sent to delivered">
                p50 delivery
              </th>
              <th className="px-3 py-1.5 text-right" title="90th percentile — worst-case delivery">
                p90 delivery
              </th>
              <th className="px-3 py-1.5 text-right" title="Median time from sent to read">
                p50 read
              </th>
            </tr>
          </thead>
          <tbody>
            {list.map((r, i) => (
              <tr key={r.template_name} className="border-t border-blue-100"
                  data-testid={`campaigns-insights-row-${i}`}>
                <td className="px-3 py-1.5">
                  <div className="font-semibold truncate max-w-[240px]" title={r.template_name}>
                    {r.template_name}
                  </div>
                  <div className="text-[10px] text-muted-foreground">
                    {r.campaigns} campaign{r.campaigns === 1 ? "" : "s"} · {r.mode}
                  </div>
                </td>
                <td className="px-3 py-1.5 text-right font-mono">{r.sent}</td>
                <td className={`px-3 py-1.5 text-right font-mono ${rateColour(r.delivery_rate)}`}>
                  {r.delivered} <span className="text-[10px] opacity-70">({fmtRate(r.delivery_rate)})</span>
                </td>
                <td className={`px-3 py-1.5 text-right font-mono ${rateColour(r.read_rate)}`}>
                  {r.read} <span className="text-[10px] opacity-70">({fmtRate(r.read_rate)})</span>
                </td>
                <td className={`px-3 py-1.5 text-right font-mono ${r.failure_rate > 0.05 ? "text-rose-700 font-bold" : "text-muted-foreground"}`}>
                  {r.failed}
                  {r.failure_rate > 0 && <span className="text-[10px] opacity-70"> ({fmtRate(r.failure_rate)})</span>}
                </td>
                <td className="px-3 py-1.5 text-right font-mono text-emerald-700">{fmtMs(r.delivery_p50_ms)}</td>
                <td className="px-3 py-1.5 text-right font-mono text-emerald-700/70">{fmtMs(r.delivery_p90_ms)}</td>
                <td className="px-3 py-1.5 text-right font-mono text-blue-700">{fmtMs(r.read_p50_ms)}</td>
              </tr>
            ))}
            {list.length === 0 && (
              <tr>
                <td colSpan={8} className="px-3 py-3 text-center text-muted-foreground text-xs italic">
                  {loading ? "Crunching…" : "No WA campaigns in the last 30 days."}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}


/** R267d · Feb 2026 — "Export failed with reasons" CSV button.
 *
 * Takes the campaign's `results` array, keeps only rows where the
 * send OR delivery failed, maps every error code through the WA
 * error legend so the CSV column reads "Customer opted out" instead
 * of "470". Downloads client-side — no round-trip needed.
 */
function ExportFailedCsvButton({ detail }) {
  if (!detail || !detail.results || detail.channel !== "whatsapp") return null;
  const failed = (detail.results || []).filter(
    (r) => r.status === "failed" || r.delivery_status === "failed");
  if (failed.length === 0) return null;
  const download = () => {
    const rows = [[
      "Lead ID", "Name", "Phone", "Status",
      "Delivery status", "Error code", "Reason (plain English)",
      "Tip / next step", "Detail", "Sent at", "Last update",
    ]];
    for (const r of failed) {
      const parsed = parseWaReason(r.reason);
      const code = r.delivery_error_code || parsed.code;
      const info = waErrorInfo(code);
      rows.push([
        r.lead_id || "", r.name || "", r.phone || "",
        r.status || "", r.delivery_status || "",
        code || "", info.label, info.tip,
        parsed.detail || r.delivery_error_message || "",
        r.at || "", r.delivery_status_at || "",
      ]);
    }
    const esc = (v) => {
      const s = String(v ?? "");
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const csv = rows.map((r) => r.map(esc).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `campaign-${detail.campaign_id?.slice(0, 8) || "wa"}-failed.csv`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };
  return (
    <button type="button"
            onClick={download}
            className="text-[11px] px-2 py-1 border border-rose-300 text-rose-800 hover:bg-rose-50 inline-flex items-center gap-1"
            data-testid="campaigns-export-failed-csv"
            title={`Export ${failed.length} failed recipient(s) with plain-English reasons.`}>
      <Download className="w-3 h-3" /> Export failed with reasons ({failed.length})
    </button>
  );
}

function StatePill({ state }) {
  const color = ({
    queued: "text-amber-700 bg-amber-100 border-amber-200",
    running: "text-blue-700 bg-blue-100 border-blue-200",
    done: "text-emerald-700 bg-emerald-100 border-emerald-200",
    failed: "text-rose-700 bg-rose-100 border-rose-200",
  })[state] || "text-neutral-600 bg-neutral-100 border-neutral-200";
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 border text-[10px] uppercase tracking-widest ${color}`} data-testid={`campaign-state-${state || "unknown"}`}>
      {state || "—"}
    </span>
  );
}

function Metric({ icon: Icon, label, value, hint, tone = "neutral" }) {
  const toneCls = ({
    neutral: "text-neutral-800",
    good: "text-emerald-700",
    warn: "text-amber-700",
    bad: "text-rose-700",
  })[tone];
  return (
    <div className="border border-border p-3 space-y-1" data-testid={`campaign-metric-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-muted-foreground">
        {Icon && <Icon className="w-3 h-3" />} {label}
      </div>
      <div className={`text-2xl font-mono tabular-nums ${toneCls}`}>{value}</div>
      {hint && <div className="text-[11px] text-muted-foreground">{hint}</div>}
    </div>
  );
}

export default function Campaigns() {
  const { activeWorkspaceId: workspaceId } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [channel, setChannel] = useState("all");
  const [search, setSearch] = useState("");
  const [detail, setDetail] = useState(null);   // full campaign doc
  const [detailLoading, setDetailLoading] = useState(false);
  // R135e · Reply feed
  const [replies, setReplies] = useState([]);
  const [repliesLoading, setRepliesLoading] = useState(false);
  // R267d · WA delivery-insights per template (last 30 days)
  const [insights, setInsights] = useState(null);
  const [insightsLoading, setInsightsLoading] = useState(false);

  const loadReplies = async () => {
    if (!workspaceId) return;
    setRepliesLoading(true);
    try {
      const r = await api.get(`/workspaces/${workspaceId}/sales/reply-feed?limit=10`);
      setReplies(r.data?.replies || []);
    } catch { /* silent */ }
    finally { setRepliesLoading(false); }
  };

  const loadInsights = async () => {
    if (!workspaceId) return;
    setInsightsLoading(true);
    try {
      const r = await api.get(
        `/workspaces/${workspaceId}/sales/campaigns/delivery-insights?days=30`);
      setInsights(r.data?.templates || []);
    } catch { /* silent — insights are best-effort */ }
    finally { setInsightsLoading(false); }
  };

  useEffect(() => { loadReplies(); loadInsights(); }, [workspaceId]);

  const load = async () => {
    if (!workspaceId) return;
    setLoading(true);
    try {
      const q = channel && channel !== "all" ? `?channel=${channel}` : "";
      const r = await api.get(`/workspaces/${workspaceId}/sales/campaigns${q}`);
      setRows(r.data?.campaigns || []);
    } catch (e) { toast.error(apiErrorMessage(e, "Failed to load campaigns")); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [workspaceId, channel]);

  // Auto-refresh every 5s while any campaign is running.
  useEffect(() => {
    const anyRunning = rows.some((r) => r.state === "running" || r.state === "queued");
    if (!anyRunning) return undefined;
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [rows]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return rows;
    return rows.filter((r) =>
      (r.name || "").toLowerCase().includes(term) ||
      (r.subject_template || "").toLowerCase().includes(term) ||
      (r.template_name || "").toLowerCase().includes(term));
  }, [rows, search]);

  const totals = useMemo(() => {
    const acc = { total: 0, sent: 0, failed: 0, opens: 0, replies: 0, skipped: 0, count: rows.length };
    rows.forEach((r) => {
      acc.total += r.total || 0;
      acc.sent += r.sent || 0;
      acc.failed += r.failed || 0;
      acc.skipped += r.skipped || 0;
      acc.opens += r.opens || 0;
      acc.replies += r.replies || 0;
    });
    acc.open_rate = acc.sent ? Math.round(acc.opens / acc.sent * 1000) / 10 : 0;
    acc.reply_rate = acc.sent ? Math.round(acc.replies / acc.sent * 1000) / 10 : 0;
    return acc;
  }, [rows]);

  const openDetail = async (campaignId) => {
    setDetailLoading(true);
    try {
      const r = await api.get(`/workspaces/${workspaceId}/sales/campaigns/${campaignId}`);
      setDetail(r.data);
    } catch (e) { toast.error(apiErrorMessage(e, "Failed to load campaign")); }
    finally { setDetailLoading(false); }
  };

  return (
    <div data-testid="campaigns-page">
      <PageHeader
        title="Campaign Analytics"
        description="Track delivery, opens, replies and bounces for every bulk WhatsApp + email blast fired from the Leads screen."
        actions={
          <div className="flex items-center gap-2">
            <a href="/app/suppressions"
               className="text-[11px] px-2 py-1 border border-border hover:bg-muted inline-flex items-center gap-1"
               data-testid="campaigns-manage-suppressions">
              <Ban className="w-3 h-3" /> Suppressions
            </a>
            <Button variant="outline" size="sm" className="rounded-none" onClick={load}
                    disabled={loading} data-testid="campaigns-refresh">
              <RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} /> Refresh
            </Button>
          </div>
        }
      />

      <div className="px-4 sm:px-6 md:px-10 py-6 space-y-4">

      <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
        <Metric icon={Rocket} label="Campaigns" value={totals.count} />
        <Metric icon={CheckCircle2} label="Sent" value={totals.sent} tone="good"
                hint={`of ${totals.total} queued`} />
        <Metric icon={MinusCircle} label="Skipped" value={totals.skipped} tone="warn" />
        <Metric icon={AlertTriangle} label="Failed" value={totals.failed} tone="bad" />
        <Metric icon={TrendingUp} label="Open rate" value={`${totals.open_rate}%`} tone="good"
                hint={`${totals.opens} opens`} />
        <Metric icon={TrendingUp} label="Reply rate" value={`${totals.reply_rate}%`} tone="good"
                hint={`${totals.replies} replies`} />
      </div>

      {/* R135e · Latest replies strip — click any to jump into the lead */}
      {/* R267d · Feb 2026 — WA delivery insights per template.
          Sits above the replies feed so ops see slow templates first. */}
      <WaDeliveryInsightsCard rows={insights} loading={insightsLoading} onRefresh={loadInsights} />
      {(replies.length > 0 || repliesLoading) && (
        <div className="border border-fuchsia-300 bg-fuchsia-50/30" data-testid="campaigns-reply-feed">
          <div className="px-3 py-1.5 border-b border-fuchsia-200 text-[10px] uppercase tracking-widest text-fuchsia-800 flex items-center gap-2">
            <Reply className="w-3 h-3" /> Latest replies
            <button onClick={loadReplies} disabled={repliesLoading}
                    className="ml-auto normal-case tracking-normal underline text-[11px]"
                    data-testid="campaigns-reply-feed-refresh">
              {repliesLoading ? "Loading…" : "Refresh"}
            </button>
          </div>
          <div className="divide-y divide-fuchsia-100">
            {replies.map((r, i) => (
              <a key={i}
                 href={r.entity_type === "lead" && r.entity_id ? `/app/leads?open=${r.entity_id}` : "#"}
                 className="flex items-center gap-3 px-3 py-1.5 text-xs hover:bg-fuchsia-100/40"
                 data-testid={`campaigns-reply-item-${i}`}>
                <Reply className="w-3 h-3 text-fuchsia-600 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold truncate">{r.lead_name || r.to || "Unknown"}</span>
                    {r.lead_company && <span className="text-[10px] text-muted-foreground truncate">· {r.lead_company}</span>}
                    {r.subject_variant && (
                      <span className={`text-[9px] px-1 border ${r.subject_variant === "A" ? "border-sky-300 text-sky-700" : "border-fuchsia-300 text-fuchsia-700"}`}>
                        {r.subject_variant}
                      </span>
                    )}
                  </div>
                  <div className="text-[11px] text-muted-foreground truncate">
                    {r.reply_preview || r.subject || "(replied)"} · <span className="italic">{r.campaign_name}</span>
                  </div>
                </div>
                <div className="text-[10px] text-muted-foreground whitespace-nowrap">{formatDateShort(r.replied_at)}</div>
              </a>
            ))}
            {replies.length === 0 && !repliesLoading && (
              <div className="px-3 py-3 text-xs text-muted-foreground italic">
                No replies yet. When a recipient hits Reply, it lands here.
              </div>
            )}
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2 pb-1">
        <div className="relative">
          <Search className="w-3.5 h-3.5 absolute left-2 top-2.5 text-muted-foreground" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)}
                 placeholder="Filter by name, subject or template…"
                 className="pl-7 h-8 rounded-none w-[280px]" data-testid="campaigns-search" />
        </div>
        <Select value={channel} onValueChange={setChannel}>
          <SelectTrigger className="h-8 w-[160px] rounded-none" data-testid="campaigns-channel-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All channels</SelectItem>
            <SelectItem value="email">Email</SelectItem>
            <SelectItem value="whatsapp">WhatsApp</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loading && rows.length === 0 ? (
        <div className="text-center py-16 text-sm text-muted-foreground">Loading…</div>
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={Rocket}
          title="No campaigns yet"
          description="Fire a bulk WhatsApp or email from the Leads screen and it will show up here with delivery + engagement stats."
        />
      ) : (
        <div className="border border-border">
          <table className="w-full text-sm">
            <thead className="bg-muted/40">
              <tr className="text-left">
                <th className="px-3 py-2 font-medium text-xs">Campaign</th>
                <th className="px-3 py-2 font-medium text-xs w-[80px]">Ch.</th>
                <th className="px-3 py-2 font-medium text-xs w-[110px]">State</th>
                <th className="px-3 py-2 font-medium text-xs text-right w-[70px]">Total</th>
                <th className="px-3 py-2 font-medium text-xs text-right w-[70px]">Sent</th>
                <th className="px-3 py-2 font-medium text-xs text-right w-[70px]">Skip</th>
                <th className="px-3 py-2 font-medium text-xs text-right w-[70px]">Fail</th>
                <th className="px-3 py-2 font-medium text-xs text-right w-[80px]">Opens</th>
                <th className="px-3 py-2 font-medium text-xs text-right w-[90px]">Open %</th>
                <th className="px-3 py-2 font-medium text-xs text-right w-[80px]">Reply %</th>
                <th className="px-3 py-2 font-medium text-xs text-right w-[80px]">Bounce %</th>
                <th className="px-3 py-2 font-medium text-xs w-[70px]">A/B</th>
                <th className="px-3 py-2 font-medium text-xs w-[130px]">Created</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => {
                const Icon = CHANNEL_ICON[r.channel] || Mail;
                return (
                  <tr key={r.id} onClick={() => openDetail(r.id)}
                      className="border-t border-border hover:bg-muted/30 cursor-pointer"
                      data-testid={`campaign-row-${r.id}`}>
                    <td className="px-3 py-2 font-medium">
                      <div className="line-clamp-1">{r.name || "Untitled"}</div>
                      <div className="text-[11px] text-muted-foreground line-clamp-1">
                        {r.subject_template || r.template_name || (r.mode === "session" ? "free text" : "")}
                      </div>
                    </td>
                    <td className="px-3 py-2">
                      <span className="inline-flex items-center gap-1 text-xs">
                        <Icon className="w-3.5 h-3.5" /> {r.channel}
                      </span>
                    </td>
                    <td className="px-3 py-2"><StatePill state={r.state} /></td>
                    <td className="px-3 py-2 text-right font-mono tabular-nums">{r.total || 0}</td>
                    <td className="px-3 py-2 text-right font-mono tabular-nums text-emerald-700">{r.sent || 0}</td>
                    <td className="px-3 py-2 text-right font-mono tabular-nums text-amber-700">{r.skipped || 0}</td>
                    <td className="px-3 py-2 text-right font-mono tabular-nums text-rose-700">{r.failed || 0}</td>
                    <td className="px-3 py-2 text-right font-mono tabular-nums">{r.opens || 0}</td>
                    <td className="px-3 py-2 text-right font-mono tabular-nums">{r.open_rate ?? 0}%</td>
                    <td className="px-3 py-2 text-right font-mono tabular-nums">{r.reply_rate ?? 0}%</td>
                    <td className="px-3 py-2 text-right font-mono tabular-nums">{r.bounce_rate ?? 0}%</td>
                    <td className="px-3 py-2">
                      {r.ab ? (
                        <span className={`text-[10px] px-1.5 py-0.5 border ${r.ab.winner === "A" ? "border-sky-300 text-sky-700 bg-sky-50" : "border-fuchsia-300 text-fuchsia-700 bg-fuchsia-50"}`}
                              title={`A: ${r.ab.A.open_rate}% opens · B: ${r.ab.B.open_rate}% opens`}
                              data-testid={`campaign-ab-winner-${r.id}`}>
                          {r.ab.winner} wins
                        </span>
                      ) : (
                        <span className="text-[10px] text-muted-foreground">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-xs text-muted-foreground">
                      <div className="flex items-center gap-1"><Clock className="w-3 h-3" /> {formatDateShort(r.created_at)}</div>
                      <div className="text-[10px]">{r.created_by_name || ""}</div>
                      <button type="button"
                              onClick={async (e) => {
                                e.stopPropagation();
                                try {
                                  const cr = await api.post(`/workspaces/${workspaceId}/sales/campaigns/${r.id}/clone`);
                                  const seed = cr.data?.draft_seed || {};
                                  // Persist the seed to localStorage so the Bulk dialog picks it up on open.
                                  try {
                                    localStorage.setItem(`bulk-email-draft:${workspaceId}:seed`, JSON.stringify({
                                      subject: seed.subject || "",
                                      subjectB: seed.subject_b || "",
                                      bodyHtml: seed.body_html || "",
                                      label: seed.campaign_label || "",
                                      intervalSec: seed.interval_seconds || 1,
                                      sendWindow: seed.send_window_local || "",
                                      defaultTz: seed.default_timezone || "",
                                      attachments: [],
                                      savedAt: Date.now(),
                                    }));
                                  } catch { /* localStorage may be full */ }
                                  toast.success("Duplicated · seed saved · open the Leads screen and hit Bulk Email");
                                } catch (err) { toast.error(apiErrorMessage(err, "Clone failed")); }
                              }}
                              className="text-[10px] mt-1 underline text-muted-foreground hover:text-primary inline-flex items-center gap-1"
                              data-testid={`campaign-duplicate-${r.id}`}
                              title="Duplicate this campaign as a fresh draft">
                        <Copy className="w-3 h-3" /> duplicate
                      </button>
                      {r.channel === "email" && (r.failed || 0) > 0 && (
                        <button type="button"
                                onClick={async (e) => {
                                  e.stopPropagation();
                                  if (!window.confirm(`Retry ${r.failed} failed recipient${r.failed === 1 ? "" : "s"} in a fresh campaign?`)) return;
                                  try {
                                    const rr = await api.post(`/workspaces/${workspaceId}/sales/campaigns/${r.id}/retry-failed`);
                                    toast.success(`Retry queued · ${rr.data?.total || 0} recipients`);
                                    load();
                                  } catch (err) { toast.error(apiErrorMessage(err, "Retry failed")); }
                                }}
                                className="text-[10px] mt-1 ml-2 underline text-rose-600 hover:text-rose-800 inline-flex items-center gap-1"
                                data-testid={`campaign-retry-failed-${r.id}`}
                                title={`Retry only the ${r.failed} failed recipients`}>
                          <RefreshCw className="w-3 h-3" /> retry {r.failed} failed
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      </div>

      <Sheet open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
        <SheetContent side="right" className="w-full sm:max-w-2xl overflow-y-auto rounded-none">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2">
              <Rocket className="w-4 h-4 text-primary" />
              {detail?.name || "Campaign"}
              {detail?.id && (
                <button type="button"
                        onClick={async () => {
                          try {
                            const r = await api.get(
                              `/workspaces/${workspaceId}/sales/campaigns/${detail.id}/export.csv`,
                              { responseType: "blob" });
                            const blob = new Blob([r.data], { type: "text/csv" });
                            const url = URL.createObjectURL(blob);
                            const a = document.createElement("a");
                            a.href = url;
                            const safe = (detail.name || "campaign").replace(/[^\w-]/g, "_").slice(0, 40);
                            a.download = `${safe}-${detail.id.slice(0, 8)}.csv`;
                            a.click();
                            URL.revokeObjectURL(url);
                            toast.success("CSV downloaded");
                          } catch (e) {
                            toast.error(apiErrorMessage(e, "Export failed"));
                          }
                        }}
                        className="ml-auto inline-flex items-center gap-1 text-[11px] font-normal px-2 py-1 border border-border hover:bg-muted"
                        data-testid="campaign-export-csv">
                  <Download className="w-3 h-3" /> CSV
                </button>
              )}
              <ExportFailedCsvButton detail={detail} />
            </SheetTitle>
          </SheetHeader>
          {detailLoading && <div className="text-sm text-muted-foreground py-8">Loading…</div>}
          {detail && (
            <div className="space-y-4 mt-4" data-testid="campaign-detail-drawer">
              <div className="grid grid-cols-3 gap-2 text-center">
                <Metric label="Total" value={detail.total || 0} />
                <Metric label="Sent" value={detail.sent || 0} tone="good" />
                <Metric label="Failed" value={detail.failed || 0} tone="bad" />
              </div>
              <div className="text-xs space-y-1">
                <div><span className="font-semibold">Channel:</span> {detail.channel}</div>
                {detail.subject_template && <div><span className="font-semibold">Subject:</span> {detail.subject_template}</div>}
                {detail.subject_template_b && <div><span className="font-semibold">Subject B:</span> {detail.subject_template_b}</div>}
                {detail.template_name && <div><span className="font-semibold">Template:</span> {detail.template_name}</div>}
                <div><span className="font-semibold">State:</span> <StatePill state={detail.state} /></div>
                {detail.send_window_local && (
                  <div><span className="font-semibold">Send window:</span> {detail.send_window_local}{detail.default_timezone ? ` (fallback ${detail.default_timezone})` : ""}</div>
                )}
                {detail.interval_seconds > 0 && (
                  <div><span className="font-semibold">Stagger:</span> {detail.interval_seconds}s / send</div>
                )}
                <div><span className="font-semibold">Created:</span> {formatDateShort(detail.created_at)} · {detail.created_by_name}</div>
                {detail.finished_at && <div><span className="font-semibold">Finished:</span> {formatDateShort(detail.finished_at)}</div>}
              </div>
              {/* R135d · A/B subject test breakdown */}
              {detail.ab && (
                <div className="border border-fuchsia-300 bg-fuchsia-50/40 p-3" data-testid="campaign-ab-breakdown">
                  <div className="text-[10px] uppercase tracking-widest text-fuchsia-700 mb-2 flex items-center gap-2">
                    A/B Subject Test
                    {detail.ab.winner && (
                      <span className="text-[10px] px-1.5 py-0.5 bg-fuchsia-600 text-white font-semibold">
                        Winner: {detail.ab.winner}
                      </span>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    {["A", "B"].map((v) => (
                      <div key={v} className={`border p-2 space-y-0.5 ${detail.ab.winner === v ? "border-fuchsia-600 bg-white" : "border-fuchsia-200"}`}>
                        <div className="font-semibold flex items-center gap-1">
                          <span className={`px-1 border text-[9px] ${v === "A" ? "border-sky-300 text-sky-700" : "border-fuchsia-300 text-fuchsia-700"}`}>
                            {v}
                          </span>
                          <span className="text-[10px] text-muted-foreground truncate">{detail.ab[v].subject}</span>
                        </div>
                        <div className="text-[11px] flex gap-3">
                          <span>Sent: <b className="font-mono">{detail.ab[v].sent}</b></span>
                          <span>Opens: <b className="font-mono">{detail.ab[v].opens}</b></span>
                          <span>Replies: <b className="font-mono">{detail.ab[v].replies}</b></span>
                        </div>
                        <div className="text-[11px]">
                          Open rate: <b className="font-mono">{detail.ab[v].open_rate}%</b> · Reply rate: <b className="font-mono">{detail.ab[v].reply_rate}%</b>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div>
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Recipient log ({detail.results?.length || 0})</div>
                <div className="border max-h-[400px] overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-muted/40 sticky top-0">
                      <tr className="text-left">
                        <th className="px-2 py-1.5">Recipient</th>
                        <th className="px-2 py-1.5">Status</th>
                        {detail.channel === "whatsapp" && (
                          <th className="px-2 py-1.5" data-testid="campaign-detail-timeline-header">Delivery timeline</th>
                        )}
                        <th className="px-2 py-1.5">Reason / ID</th>
                        <th className="px-2 py-1.5">At</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(detail.results || []).slice().reverse().map((r, i) => {
                        const parsed = parseWaReason(r.reason);
                        const errInfo = (r.status === "failed" || r.delivery_status === "failed")
                          ? waErrorInfo(r.delivery_error_code || parsed.code) : null;
                        return (
                        <tr key={i} className="border-t border-border" data-testid={`campaign-detail-row-${i}`}>
                          <td className="px-2 py-1.5">
                            <div className="font-medium">{r.name || "—"}</div>
                            <div className="text-[10px] text-muted-foreground">{r.email || r.phone || ""}</div>
                          </td>
                          <td className="px-2 py-1.5">
                            <span className={
                              r.status === "sent" ? "text-emerald-700" :
                              r.status === "failed" ? "text-rose-700" :
                              "text-amber-700"
                            }>{r.status}</span>
                          </td>
                          {detail.channel === "whatsapp" && (
                            <td className="px-2 py-1.5" data-testid={`campaign-detail-timeline-${i}`}>
                              <WaDeliveryTimeline row={r} />
                            </td>
                          )}
                          <td className="px-2 py-1.5 text-muted-foreground">
                            {errInfo ? (
                              <div className="flex flex-col cursor-help group max-w-[280px]" title={`${errInfo.label} · ${errInfo.tip}`}>
                                <span className="font-semibold text-rose-700">
                                  {errInfo.label}
                                </span>
                                <span className="text-[10px] text-rose-600/80 group-hover:text-rose-800 line-clamp-2">
                                  {errInfo.tip}
                                </span>
                                {(r.delivery_error_code || parsed.code) && (
                                  <span className="text-[9px] font-mono text-muted-foreground mt-0.5">
                                    code · {r.delivery_error_code || parsed.code}
                                  </span>
                                )}
                              </div>
                            ) : (
                              r.reason || r.message_id || ""
                            )}
                          </td>
                          <td className="px-2 py-1.5 text-muted-foreground">{formatDateShort(r.at)}</td>
                        </tr>
                        );
                      })}
                      {(!detail.results || detail.results.length === 0) && (
                        <tr><td colSpan={detail.channel === "whatsapp" ? 5 : 4} className="px-2 py-4 text-center text-muted-foreground">No sends yet…</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
